/**
 * Best-effort frontend runtime-error telemetry.
 *
 * `reportClientError` builds a *sanitized* event and POSTs it to
 * `/api/client-telemetry` (the backend re-sanitizes against a strict allowlist —
 * see `app/backend/routes/client_telemetry.py`). This is purely additive to the
 * console `logger`: it never replaces logging and never throws.
 *
 * PRIVACY (ISO): we emit identifiers + bounded operational context ONLY. Never
 * tokens, api keys, connection strings, JWTs, raw prompts, document contents, or
 * chat transcripts. The shape below is the entire payload — nothing else is sent.
 */
import { api, getLastCorrelationId } from "@/lib/api-client"

// Keep the stack bounded so a deep async trace can't blow the payload. The
// backend caps `stack` again at 8000 chars; we cap smaller here to save bytes.
const MAX_STACK_LEN = 4000
const MAX_MESSAGE_LEN = 1000

// Dedup window: the same error frequently fires through a React error boundary,
// react-query's global onError, AND window.onerror in quick succession. We emit
// it once per window.
const DEDUP_WINDOW_MS = 5000

// The endpoint, relative to the api-client baseURL ("/api" or
// "http://localhost:<port>/api"). For the sendBeacon path we resolve an absolute
// URL because Beacon ignores the axios baseURL.
const TELEMETRY_PATH = "/client-telemetry"

/** Event types the backend accepts (must match VALID_TYPES server-side). */
export type ClientTelemetryType = "exception" | "error_event"

/** Subset of feedback's `client_info` — lightweight, no PII beyond UA/locale. */
export type TelemetryClientInfo = {
  user_agent: string
  viewport: string
  language: string
  timezone?: string
  online?: boolean
  connection_type?: string
  platform?: string
}

/**
 * The exact wire shape. Every key here is on the backend ALLOWLIST; anything not
 * listed would be dropped server-side anyway, so we never add more.
 */
export type ClientTelemetryEvent = {
  type: ClientTelemetryType
  name: string
  message: string
  stack?: string
  route: string
  app_version: string
  environment: string
  correlation_id?: string
  session_id?: string
  message_id?: string
  client_info: TelemetryClientInfo
  ts: string
}

/** Optional caller-supplied context. None of this is required. */
export type ReportClientErrorContext = {
  type?: ClientTelemetryType
  /** Correlation id if the caller already has one (else we fall back). */
  correlationId?: string
  sessionId?: string
  messageId?: string
  /** Coarse origin label for debugging, folded into `name` only. */
  source?: string
}

// ---------------------------------------------------------------------------
// Dedup — exported for unit testing.
// ---------------------------------------------------------------------------

const _seen = new Map<string, number>()

/** First non-empty stack frame, trimmed — used as a fingerprint component. */
function topStackFrame(stack: string | undefined): string {
  if (!stack) return ""
  for (const line of stack.split("\n")) {
    const trimmed = line.trim()
    // Skip the `Error: message` header line; the first `at …` frame is best.
    if (trimmed && trimmed !== "Error" && !trimmed.startsWith("Error:")) {
      return trimmed
    }
  }
  return ""
}

/**
 * Stable, non-cryptographic fingerprint of an error occurrence. Same
 * name+message+top-frame+route hash to the same value regardless of call site,
 * so a single error firing through multiple channels dedups to one emission.
 */
export function fingerprintError(parts: {
  name: string
  message: string
  stack?: string
  route: string
}): string {
  const basis = [parts.name, parts.message, topStackFrame(parts.stack), parts.route].join(" ")
  // djb2 — tiny, dependency-free, good enough for dedup bucketing.
  let hash = 5381
  for (let i = 0; i < basis.length; i++) {
    hash = (hash * 33) ^ basis.charCodeAt(i)
  }
  // Unsigned hex.
  return (hash >>> 0).toString(16)
}

/**
 * Returns true if this fingerprint was emitted within `windowMs` and should be
 * skipped. On a miss (or expiry) it records `now` and returns false. Exported so
 * the dedup behaviour can be unit-tested deterministically via the `clock` arg.
 */
export function shouldEmit(
  fingerprint: string,
  now: number = Date.now(),
  windowMs: number = DEDUP_WINDOW_MS
): boolean {
  const last = _seen.get(fingerprint)
  if (last !== undefined && now - last < windowMs) {
    return false
  }
  _seen.set(fingerprint, now)
  // Opportunistic GC so the map can't grow unbounded across a long session.
  if (_seen.size > 500) {
    for (const [key, ts] of _seen) {
      if (now - ts >= windowMs) _seen.delete(key)
    }
  }
  return true
}

/** Test-only: reset the dedup state between cases. */
export function resetDedupForTests(): void {
  _seen.clear()
}

// ---------------------------------------------------------------------------
// Payload construction
// ---------------------------------------------------------------------------

function cap(value: string | undefined, max: number): string | undefined {
  if (value === undefined) return undefined
  return value.length > max ? value.slice(0, max) : value
}

/** App version baked in at build time (see vite.config.ts `define`). */
function appVersion(): string {
  return (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "0.0.0"
}

/**
 * Minimal client_info — a deliberate subset of feedback's `buildClientInfo`.
 * No URL/referrer/page_title (could leak query params / doc titles); no
 * screen/hardware fingerprinting beyond what aids triage. Best-effort: any
 * missing browser API degrades silently.
 */
function buildTelemetryClientInfo(): TelemetryClientInfo {
  const info: TelemetryClientInfo = {
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    viewport: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
    language: typeof navigator !== "undefined" ? navigator.language : "",
  }
  try {
    info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    /* noop */
  }
  if (typeof navigator !== "undefined") {
    info.online = navigator.onLine
    const connection = (navigator as Navigator & { connection?: { effectiveType?: string } })
      .connection
    if (connection?.effectiveType) info.connection_type = connection.effectiveType
    if (navigator.platform) info.platform = navigator.platform
  }
  return info
}

/** Coerce an unknown thrown value into a name/message/stack triple. */
function normalizeError(error: unknown): { name: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return { name: error.name || "Error", message: error.message || "", stack: error.stack }
  }
  if (typeof error === "string") {
    return { name: "Error", message: error }
  }
  try {
    return { name: "Error", message: JSON.stringify(error) }
  } catch {
    return { name: "Error", message: String(error) }
  }
}

function buildEvent(
  error: unknown,
  context: ReportClientErrorContext | undefined
): ClientTelemetryEvent {
  const { name, message, stack } = normalizeError(error)
  const route = typeof window !== "undefined" ? window.location.pathname : ""
  const decoratedName = context?.source ? `${name} (${context.source})` : name

  return {
    type: context?.type ?? "exception",
    name: cap(decoratedName, 200) ?? "Error",
    message: cap(message, MAX_MESSAGE_LEN) ?? "",
    stack: cap(stack, MAX_STACK_LEN),
    route,
    app_version: appVersion(),
    environment: import.meta.env.MODE,
    correlation_id: context?.correlationId ?? getLastCorrelationId() ?? undefined,
    session_id: context?.sessionId,
    message_id: context?.messageId,
    client_info: buildTelemetryClientInfo(),
    ts: new Date().toISOString(),
  }
}

// ---------------------------------------------------------------------------
// Transport — best-effort, never throws.
// ---------------------------------------------------------------------------

/**
 * Fire the event via the shared axios client so the Authorization header is
 * attached. We deliberately do NOT use `navigator.sendBeacon`: the
 * `/api/client-telemetry` route is `@authenticated`, and Beacon can't set the
 * bearer header, so a beacon would just 401 and be dropped. That means the
 * unload/offline path can occasionally lose a terminal error — accepted for now;
 * a cookie/unauthenticated ingest variant that makes Beacon viable is tracked as
 * a follow-up. Swallows all errors.
 */
async function send(event: ClientTelemetryEvent): Promise<void> {
  try {
    // The axios response interceptor returns `response.data`; we ignore it.
    await api.post(TELEMETRY_PATH, event)
  } catch {
    // Telemetry is fire-and-forget — never surface a UI error for it.
  }
}

/**
 * Report a runtime error to backend telemetry. Best-effort and idempotent within
 * the dedup window: never throws, returns immediately, and emits at most once per
 * fingerprint per ~5s.
 *
 * @param error  Any thrown value (Error, string, or arbitrary).
 * @param context  Optional ids/source. None required.
 */
export function reportClientError(error: unknown, context?: ReportClientErrorContext): void {
  try {
    const event = buildEvent(error, context)
    // Fingerprint the UNDECORATED name. `event.name` folds in `context.source`
    // (e.g. "Error (react-query)"), which differs when the same error fires
    // through the error boundary vs react-query vs window.onerror — using it
    // here would defeat the dedup window for exactly that multi-channel case.
    const fp = fingerprintError({
      name: normalizeError(error).name,
      message: event.message,
      stack: event.stack,
      route: event.route,
    })
    if (!shouldEmit(fp)) return

    void send(event)
  } catch {
    // Absolutely never let telemetry break the caller.
  }
}

// ---------------------------------------------------------------------------
// Global bootstrap — window.onerror + unhandledrejection.
// ---------------------------------------------------------------------------

let globalHandlersRegistered = false

/**
 * Register window-level error listeners exactly once. Idempotent — safe to call
 * from app bootstrap (and a no-op outside the browser, e.g. in tests/SSR).
 */
export function registerGlobalErrorTelemetry(): void {
  if (globalHandlersRegistered) return
  if (typeof window === "undefined") return
  globalHandlersRegistered = true

  window.addEventListener("error", (event: ErrorEvent) => {
    // Resource-load errors (img/script 404s) surface as ErrorEvents with no
    // `error` and an empty message; skip them — they're noise, not runtime bugs.
    const err = event.error ?? (event.message ? event.message : undefined)
    if (err === undefined) return
    reportClientError(err, { source: "window.onerror" })
  })

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    reportClientError(event.reason, { source: "unhandledrejection" })
  })
}
